import { Link } from "react-router"
import {
  AlertTriangle,
  ArrowUpRight,
  Building07,
  CurrencyDollar,
  LayersThree01,
  Settings01,
  UserPlus01,
  Users03,
} from "@untitledui/icons"
import type { FC } from "react"
import { motion } from "motion/react"
import { useSetupTasks, type SetupTaskId } from "@/lib/setup-tasks/use-setup-tasks"
import { cx } from "@/lib/utils/cx"

// Persistent setup-task warnings on the dashboard. Two visual modes:
//   - amber  : at least one amber-tone task pending (fees not reviewed,
//              body shop not connected). These are the same prerequisites
//              the estimate gate enforces. Banner reads as a real warning.
//   - neutral: only neutral-tone tasks pending (invite team, add locations).
//              Friendly nudge, not a warning.
//
// Each task auto-clears when its underlying condition is met; saving fees
// in Settings flips the amber banner away in the same render.

const ICONS: Record<SetupTaskId, FC<{ className?: string; "aria-hidden"?: boolean }>> = {
  invite_members: UserPlus01,
  invite_group_leadership: Users03,
  connect_bodyshop: Building07,
  setup_fees: CurrencyDollar,
  add_locations: LayersThree01,
}

export function SetupTasksBanner({ className }: { className?: string }) {
  const { pending, hasPending, hasBlocking, isLoading } = useSetupTasks()
  if (isLoading || !hasPending) return null

  // Amber tasks come first so the most urgent items are at the top of the
  // list — visual order reinforces the urgency the tone communicates.
  const ordered = [...pending].sort((a, b) => {
    if (a.tone === b.tone) return 0
    return a.tone === "amber" ? -1 : 1
  })

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={cx(
        "rounded-2xl border shadow-sm overflow-hidden",
        hasBlocking
          ? "border-amber-300/70 bg-amber-50/40 ring-1 ring-amber-200/40"
          : "border-emerald-200/70 bg-white",
        className,
      )}
    >
      <div className={cx("px-5 pt-5 pb-3 flex items-start gap-3.5")}>
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            hasBlocking
              ? "bg-gradient-to-br from-amber-500 to-amber-700 shadow-amber-600/25"
              : "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-600/25",
          )}
        >
          {hasBlocking ? (
            <AlertTriangle className="size-5 text-white" aria-hidden />
          ) : (
            <Settings01 className="size-5 text-white" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={cx(
              "text-[15px] font-semibold",
              hasBlocking ? "text-amber-950" : "text-zinc-900",
            )}
          >
            {hasBlocking ? "Action needed before you can run estimates" : "Finish setting up"}
          </h3>
          <p
            className={cx(
              "text-sm mt-0.5",
              hasBlocking ? "text-amber-900/75" : "text-zinc-500",
            )}
          >
            {hasBlocking
              ? `${ordered.filter((t) => t.tone === "amber").length} blocking ${
                  ordered.filter((t) => t.tone === "amber").length === 1 ? "issue" : "issues"
                } — fix before sending estimates.`
              : pending.length === 1
                ? "One task left to wrap up your workspace."
                : `${pending.length} tasks left to wrap up your workspace.`}
          </p>
        </div>
      </div>
      <div
        className={cx(
          "border-t",
          hasBlocking ? "border-amber-200/60 bg-white/60" : "border-zinc-100",
        )}
      >
        {ordered.map((task, i) => {
          const Icon = ICONS[task.id]
          const isAmber = task.tone === "amber"
          return (
            <Link
              key={task.id}
              to={task.to}
              className={cx(
                "flex items-center gap-3 px-5 py-3.5 transition-colors duration-100 group",
                isAmber ? "hover:bg-amber-50/70" : "hover:bg-zinc-50/70",
                i !== ordered.length - 1 &&
                  (hasBlocking ? "border-b border-amber-200/50" : "border-b border-zinc-100"),
              )}
            >
              <div
                className={cx(
                  "size-9 rounded-xl grid place-items-center shrink-0 transition-colors duration-150",
                  isAmber
                    ? "bg-amber-100 group-hover:bg-amber-200/70"
                    : "bg-zinc-50 group-hover:bg-emerald-50",
                )}
              >
                <Icon
                  className={cx(
                    "size-4 transition-colors duration-150",
                    isAmber
                      ? "text-amber-700 group-hover:text-amber-900"
                      : "text-zinc-500 group-hover:text-emerald-700",
                  )}
                  aria-hidden
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p
                    className={cx(
                      "text-sm font-semibold",
                      isAmber ? "text-amber-950" : "text-zinc-900",
                    )}
                  >
                    {task.title}
                  </p>
                  {isAmber && (
                    <span className="text-[10.5px] font-bold uppercase tracking-wide bg-amber-200/80 text-amber-900 px-1.5 py-0.5 rounded">
                      Required
                    </span>
                  )}
                </div>
                <p
                  className={cx(
                    "text-xs mt-0.5",
                    isAmber ? "text-amber-900/75" : "text-zinc-500",
                  )}
                >
                  {task.description}
                </p>
              </div>
              <span
                className={cx(
                  "hidden sm:flex items-center gap-1 text-xs font-semibold transition-colors duration-150 shrink-0 px-2.5 py-1.5 rounded-lg",
                  isAmber
                    ? "bg-amber-600 text-white group-hover:bg-amber-700"
                    : "text-zinc-400 group-hover:text-zinc-700",
                )}
              >
                {task.cta}
                <ArrowUpRight className="size-3" aria-hidden />
              </span>
              <ArrowUpRight
                className={cx(
                  "sm:hidden size-4 transition-colors duration-150 shrink-0",
                  isAmber
                    ? "text-amber-600 group-hover:text-amber-800"
                    : "text-zinc-300 group-hover:text-zinc-500",
                )}
                aria-hidden
              />
            </Link>
          )
        })}
      </div>
    </motion.div>
  )
}
